import React from 'react';
import Button from '@ajat/design-system/Button';
import Notification from '@ajat/design-system/Notification';
import { useNotification } from '@ajat/design-system/Notification/useNotification';
import { ComponentPage, DemoCard, DemoRow, Demo } from '../shared';

export const meta = {
  component: Notification,
  title: 'Notification',
  group: 'Feedback',
  importPath: '@ajat/design-system/Notification',
  figmaNode: '3998:905',
  description:
    'Toast-style feedback. Slides in from the left, shows a timer bar shrinking from 100% to 0%, then auto-dismisses. Hovering pauses the timer; the close button dismisses immediately. Three types: success, error and warning.',
  props: [
    ['type', "'success' | 'error' | 'warning'", "'success'", 'Visual variant — sets the info box and text colour'],
    ['title', 'string', "''", 'Heading text (typo-h4)'],
    ['message', 'string', "''", 'Info box text (typo-body, coloured to match the type)'],
    ['duration', 'number', '4000', 'Auto-dismiss time in ms; the timer bar shrinks over this period'],
    ['onClose', 'func', '—', 'Called when the notification is dismissed, automatically or manually'],
    ['preview', 'bool', 'false', 'Static mode — no animation, no timer, no fixed positioning'],
    ['closeLabel', 'string', "'Close notification'", 'Screen-reader name of the X button (also a pushNotification option)'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  propsNote:
    'The imperative API (useNotification / pushNotification) takes the same type, title, message and duration fields.',
  usage: [
    {
      title: 'Imperative — recommended',
      code: `// 1. Mount the provider once at the app root
import { NotificationProvider } from '@ajat/design-system/Notification/useNotification';

function App() {
  return (
    <>
      <Routes />
      <NotificationProvider />
    </>
  );
}

// 2. Use the hook anywhere below it
import { useNotification } from '@ajat/design-system/Notification/useNotification';

function CartButton() {
  const notify = useNotification();

  const handleAdd = () => {
    addToCart(item);
    notify({
      type: 'success',
      title: 'The cart was updated',
      message: 'Total price is 343,20 KR',
    });
  };

  return <Button onClick={handleAdd}>Add to cart</Button>;
}`,
    },
    {
      title: 'Standalone push — non-React contexts',
      code: `// Services, event handlers, anywhere without hooks
import { pushNotification } from '@ajat/design-system/Notification/useNotification';

pushNotification({
  type: 'error',
  title: 'Connection lost',
  message: 'Please check your internet connection.',
  duration: 6000,
});`,
    },
    {
      title: 'Declarative — single toast',
      code: `import Notification from '@ajat/design-system/Notification';

// Manages its own lifecycle; you own the visibility flag
{showToast && (
  <Notification
    type="success"
    title="Profile updated"
    message="Your changes have been saved."
    duration={4000}
    onClose={() => setShowToast(false)}
  />
)}`,
    },
  ],
};

export default function NotificationPage() {
  const notify = useNotification();

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Live demo" hint="fires in the bottom-left corner">
        <DemoRow align="center">
          <Button
            size="secondary"
            variant="main"
            onClick={() =>
              notify({
                type: 'success',
                title: 'The cart was updated',
                message: 'Total price is 343,20 KR',
              })
            }
          >
            Success
          </Button>
          <Button
            size="secondary"
            variant="outline"
            onClick={() =>
              notify({
                type: 'error',
                title: 'We were unable to update your cart',
                message: 'It looks like some items are no longer available',
              })
            }
          >
            Error
          </Button>
          <Button
            size="secondary"
            variant="outline"
            onClick={() =>
              notify({
                type: 'warning',
                title: 'You have last product in your cart',
                message: 'It looks like some items are no longer available',
                duration: 6000,
              })
            }
          >
            Warning (6s)
          </Button>
        </DemoRow>
      </DemoCard>

      <DemoCard title="Types" hint="preview mode — static, no timer">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-ui-medium-l)' }}>
          <Demo caption="SUCCESS">
            <Notification
              preview
              type="success"
              title="The cart was updated"
              message="Total price is 343,20 KR"
            />
          </Demo>
          <Demo caption="ERROR">
            <Notification
              preview
              type="error"
              title="We were unable to update your cart"
              message="It looks like some items are no longer available"
            />
          </Demo>
          <Demo caption="WARNING">
            <Notification
              preview
              type="warning"
              title="You have last product in your cart"
              message="It looks like some items are no longer available"
            />
          </Demo>
        </div>
      </DemoCard>
    </ComponentPage>
  );
}
